import type { Artikelstamm, ArtikelstammEingabe } from './api';
import type { Speicher } from './belegnummern';
import { ICON_NAMEN, type IconName } from './icons';
import { normalisiereEan } from './lager';
import type { StammAblage } from './stamm-speicher';
import type { Position, Steuersatz } from './warenkorb';

/**
 * Schnellwahl der Touch-Kasse: Warengruppen (Spalte in der Mitte) mit
 * farbigen Artikel-Kacheln (Raster rechts), je Kasse auf dem Gerät gespeichert.
 * Kein Artikelstamm nötig – Lager/Backoffice kommen später. Ein Tipp auf eine
 * Kachel legt eine Position in den Bon.
 */
export const FARBEN = {
  blau: '#1f3a5f',
  tuerkis: '#0e8f8a',
  gruen: '#2e8b3d',
  orange: '#e8701a',
  rot: '#d23b3b',
  lila: '#8e44ad',
  braun: '#8c6a56',
  hellblau: '#2a9fd6',
  grau: '#5a6572',
} as const;
export type Farbe = keyof typeof FARBEN;
export const FARB_NAMEN = Object.keys(FARBEN) as Farbe[];

export interface Warengruppe {
  name: string;
  farbe: Farbe;
  icon?: IconName;
  /** USt-Vorschlag für neue Kacheln dieser Gruppe (aus dem Artikelstamm am Server). */
  steuersatz?: Steuersatz;
}

export interface Schnellwahl {
  bezeichnung: string;
  /** Einzelpreis brutto in Cent. */
  bruttoCent: number;
  steuersatz: Steuersatz;
  /** Name der Warengruppe. */
  gruppe: string;
  /** Eigene Farbe; sonst Farbe der Gruppe. */
  farbe?: Farbe;
  /** Eigenes Icon; sonst Icon der Gruppe. */
  icon?: IconName;
  /** Steuersatz bei „Außer Haus" (Mitnahme/Lieferung), wenn er abweicht. */
  steuersatzAusserHaus?: Steuersatz;
  /** Artikel-ID des Serverstamms (für den Lagerabgang); Gerätekacheln ohne. */
  artikelId?: number;
  /** EAN-8/EAN-13 für den Scanner (aus dem Serverstamm). */
  ean?: string;
  /** Barcode wie auf der Ware (Text, führende Nullen; aus dem Serverstamm, Migration 0050). */
  barcode?: string;
  /** Artikelnummer (Text; aus dem Serverstamm). */
  artikelnummer?: string;
  /** Änderungscursor des Serverartikels (P7) – geht als `artikelStand` an die Position. */
  artikelStand?: number;
  /** Im Schnellwahl-Profil der Kasse ausgeblendet (P10): nicht in der Gruppenansicht. */
  ausgeblendet?: boolean;
}

/** Schnellwahl-Profil der Kasse (P10): nur Darstellung – nie Preis, Steuer oder Sperre. */
export interface KassenProfil {
  id: number;
  name: string;
  version: number;
  kacheln: {
    art: 'gruppe' | 'artikel';
    objektId: number;
    reihenfolge: number;
    farbe: string | null;
    icon: string | null;
    sichtbar: boolean;
  }[];
}

/** Gesperrter Artikel des Serverstamms: sichtbar, aber nicht verkaufbar. */
export type GesperrteKachel = Schnellwahl & { grund: string };

const EAN_FORMAT = /^(\d{8}|\d{13})$/;

export interface SchnellwahlKonfig {
  gruppen: Warengruppe[];
  /** Verkaufbare Kacheln – nur diese landen über Tippen, Suche oder Scanner im Bon. */
  kacheln: Schnellwahl[];
  /**
   * Gesperrte Artikel (Verkaufssperre, unbestätigter Steuersatz): bewusst getrennt von
   * `kacheln`, damit kein bestehender Verkaufsweg sie in den Bon legt. Anzeige mit Grund.
   */
  gesperrt?: GesperrteKachel[];
  /**
   * Datenstand des Servers, auf dem diese Schnellwahl beruht (P2). Er gehört untrennbar zur
   * Konfiguration: übertragen wird genau dieses Paar, sonst lehnt der Server ab (409/428) –
   * so überschreibt ein veralteter Stand (anderes Fenster, Offline-Gerät) nie Neueres.
   */
  basisVersion?: number;
}

export const OHNE_GRUPPE = 'Allgemein';

function schluessel(kassenId: string): string {
  return `bilanx.schnellwahl.${kassenId}`;
}

const STEUERSAETZE = ['20', '10', '13', '0', '19', '4,9'];

function istFarbe(x: unknown): x is Farbe {
  return typeof x === 'string' && Object.hasOwn(FARBEN, x);
}

function istIcon(x: unknown): x is IconName {
  return typeof x === 'string' && (ICON_NAMEN as readonly string[]).includes(x);
}

function istSteuersatz(x: unknown): x is Steuersatz {
  return typeof x === 'string' && STEUERSAETZE.includes(x);
}

function zuKachel(x: unknown): Schnellwahl | null {
  if (typeof x !== 'object' || x === null) {
    return null;
  }
  const s = x as Record<string, unknown>;
  if (
    typeof s.bezeichnung !== 'string' ||
    s.bezeichnung.trim() === '' ||
    typeof s.bruttoCent !== 'number' ||
    !Number.isInteger(s.bruttoCent) ||
    typeof s.steuersatz !== 'string' ||
    !STEUERSAETZE.includes(s.steuersatz)
  ) {
    return null;
  }
  return {
    bezeichnung: s.bezeichnung,
    bruttoCent: s.bruttoCent,
    steuersatz: s.steuersatz as Steuersatz,
    gruppe: typeof s.gruppe === 'string' && s.gruppe.trim() !== '' ? s.gruppe : OHNE_GRUPPE,
    ...(istFarbe(s.farbe) ? { farbe: s.farbe } : {}),
    ...(istIcon(s.icon) ? { icon: s.icon } : {}),
    ...(istSteuersatz(s.steuersatzAusserHaus)
      ? { steuersatzAusserHaus: s.steuersatzAusserHaus }
      : {}),
    ...(typeof s.artikelId === 'number' && Number.isInteger(s.artikelId)
      ? { artikelId: s.artikelId }
      : {}),
    ...(typeof s.ean === 'string' && EAN_FORMAT.test(s.ean) ? { ean: s.ean } : {}),
    ...(typeof s.barcode === 'string' && s.barcode !== '' ? { barcode: s.barcode } : {}),
    ...(typeof s.artikelnummer === 'string' && s.artikelnummer !== ''
      ? { artikelnummer: s.artikelnummer }
      : {}),
    // P7/P10: Cursor (für den Preisschutz S-5) und Profil-Sichtbarkeit überstehen den Cache
    ...(typeof s.artikelStand === 'number' && Number.isInteger(s.artikelStand)
      ? { artikelStand: s.artikelStand }
      : {}),
    ...(s.ausgeblendet === true ? { ausgeblendet: true } : {}),
  };
}

function zuGesperrt(x: unknown): GesperrteKachel | null {
  const k = zuKachel(x);
  if (k === null) {
    return null;
  }
  const g = (x as Record<string, unknown>).grund;
  return { ...k, grund: typeof g === 'string' && g !== '' ? g : 'Verkaufssperre' };
}

function zuGruppe(x: unknown): Warengruppe | null {
  if (typeof x !== 'object' || x === null) {
    return null;
  }
  const g = x as Record<string, unknown>;
  if (typeof g.name !== 'string' || g.name.trim() === '') {
    return null;
  }
  return {
    name: g.name,
    farbe: istFarbe(g.farbe) ? g.farbe : 'blau',
    ...(istIcon(g.icon) ? { icon: g.icon } : {}),
    ...(istSteuersatz(g.steuersatz) ? { steuersatz: g.steuersatz } : {}),
  };
}

/** Gruppen ergänzen, die nur über Kacheln bekannt sind (Reihenfolge: zuerst gepflegte Gruppen). */
function vervollstaendige(konfig: SchnellwahlKonfig): SchnellwahlKonfig {
  const gruppen = [...konfig.gruppen];
  for (const k of konfig.kacheln) {
    // Ausgeblendete Kacheln (P10) erzeugen keinen Reiter – ihre Gruppe ist im Profil verborgen
    if (k.ausgeblendet !== true && !gruppen.some((g) => g.name === k.gruppe)) {
      gruppen.push({ name: k.gruppe, farbe: 'blau' });
    }
  }
  if (konfig.gesperrt !== undefined) {
    gesperrteZu.set(konfig.kacheln, konfig.gesperrt);
  }
  return {
    gruppen,
    kacheln: konfig.kacheln,
    ...(konfig.gesperrt === undefined ? {} : { gesperrt: konfig.gesperrt }),
    ...(konfig.basisVersion === undefined ? {} : { basisVersion: konfig.basisVersion }),
  };
}

function istBasisVersion(v: unknown): v is number {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}

/**
 * Gesperrte Artikel zur Kachelliste derselben Konfiguration: So erkennt auch die einfache
 * Suche (sucheKacheln(konfig.kacheln, …), z. B. Enter im Suchfeld), dass ein Code einem
 * gesperrten Artikel gehört, und legt keinen anderen Artikel in den Bon.
 */
const gesperrteZu = new WeakMap<readonly Schnellwahl[], readonly GesperrteKachel[]>();

export function ladeSchnellwahl(speicher: Speicher, kassenId: string): SchnellwahlKonfig {
  const roh = speicher.getItem(schluessel(kassenId));
  if (roh === null) {
    return { gruppen: [], kacheln: [] };
  }
  try {
    const daten: unknown = JSON.parse(roh);
    // Altes Format: reine Kachelliste ohne Gruppen.
    if (Array.isArray(daten)) {
      return vervollstaendige({
        gruppen: [],
        kacheln: daten.map(zuKachel).filter((k): k is Schnellwahl => k !== null),
      });
    }
    if (typeof daten !== 'object' || daten === null) {
      return { gruppen: [], kacheln: [] };
    }
    const d = daten as Record<string, unknown>;
    return vervollstaendige({
      gruppen: Array.isArray(d.gruppen)
        ? d.gruppen.map(zuGruppe).filter((g): g is Warengruppe => g !== null)
        : [],
      kacheln: Array.isArray(d.kacheln)
        ? d.kacheln.map(zuKachel).filter((k): k is Schnellwahl => k !== null)
        : [],
      ...(Array.isArray(d.gesperrt)
        ? {
            gesperrt: d.gesperrt.map(zuGesperrt).filter((x): x is GesperrteKachel => x !== null),
          }
        : {}),
      ...(istBasisVersion(d.basisVersion) ? { basisVersion: d.basisVersion } : {}),
    });
  } catch {
    return { gruppen: [], kacheln: [] };
  }
}

/**
 * Gerätekonfiguration speichern. Ein sehr großer Stamm (über MAX_STAMM_LOKAL, z. B. aus dem
 * Artikelimport) wird nicht in den localStorage geschrieben – er gehört dem Server bzw.
 * IndexedDB und würde den Platz für Bon-Sicherung und Belegnummern belegen. Dann bleibt eine
 * leere Konfiguration (kein veralteter Stand). false = nicht (vollständig) gespeichert.
 */
export function speichereSchnellwahl(
  speicher: Speicher,
  kassenId: string,
  konfig: SchnellwahlKonfig,
): boolean {
  const json = JSON.stringify(konfig);
  const passt = json.length <= MAX_STAMM_LOKAL;
  try {
    speicher.setItem(schluessel(kassenId), passt ? json : '{"gruppen":[],"kacheln":[]}');
    return passt;
  } catch {
    return false;
  }
}

/** Farbe einer Kachel: eigene Farbe, sonst die ihrer Gruppe. */
export function kachelFarbe(k: Schnellwahl, gruppen: readonly Warengruppe[]): string {
  const name = k.farbe ?? gruppen.find((g) => g.name === k.gruppe)?.farbe ?? 'blau';
  return FARBEN[name];
}

/** Icon einer Kachel: eigenes, sonst das ihrer Gruppe, sonst keins. */
export function kachelIcon(k: Schnellwahl, gruppen: readonly Warengruppe[]): IconName | null {
  return k.icon ?? gruppen.find((g) => g.name === k.gruppe)?.icon ?? null;
}

/** Beispiel-Konfiguration (Gastronomie) zum schnellen Start; wird vom Betreiber angepasst. */
export function beispielGastro(): SchnellwahlKonfig {
  const k = (
    gruppe: string,
    bezeichnung: string,
    bruttoCent: number,
    steuersatz: Steuersatz,
  ): Schnellwahl => ({ gruppe, bezeichnung, bruttoCent, steuersatz });
  return {
    gruppen: [
      { name: 'Alkoholfrei', farbe: 'tuerkis', icon: 'wasser' },
      { name: 'Bier', farbe: 'orange', icon: 'bier' },
      { name: 'Wein', farbe: 'lila', icon: 'wein' },
      { name: 'Spirituosen', farbe: 'rot', icon: 'flasche' },
      { name: 'Heißgetränke', farbe: 'braun', icon: 'kaffee' },
      { name: 'Speisen', farbe: 'gruen', icon: 'teller' },
    ],
    kacheln: [
      k('Alkoholfrei', 'Cola 0,3', 320, '20'),
      k('Alkoholfrei', 'Limonade 0,3', 320, '20'),
      k('Alkoholfrei', 'Tafelwasser 0,3', 260, '20'),
      { ...k('Alkoholfrei', 'Apfelschorle 0,3', 330, '20'), icon: 'saft' },
      { ...k('Alkoholfrei', 'Orangensaft 0,3', 350, '20'), icon: 'saft' },
      k('Bier', 'Pils 0,25', 330, '20'),
      k('Bier', 'Pils 0,5', 480, '20'),
      k('Bier', 'Weißbier 0,5', 490, '20'),
      k('Bier', 'Radler 0,5', 470, '20'),
      k('Wein', 'Grüner Veltliner 1/8', 390, '20'),
      k('Wein', 'Zweigelt 1/8', 390, '20'),
      k('Wein', 'Spritzer 1/4', 380, '20'),
      { ...k('Spirituosen', 'Gin Tonic', 790, '20'), icon: 'cocktail' },
      k('Spirituosen', 'Jägermeister 2 cl', 350, '20'),
      k('Heißgetränke', 'Espresso', 290, '10'),
      k('Heißgetränke', 'Melange', 390, '10'),
      k('Heißgetränke', 'Cappuccino', 420, '10'),
      k('Heißgetränke', 'Tee', 340, '10'),
      k('Speisen', 'Wiener Schnitzel', 1690, '10'),
      k('Speisen', 'Gulasch', 1290, '10'),
      { ...k('Speisen', 'Apfelstrudel', 450, '10'), icon: 'kuchen' },
      k('Speisen', 'Frankfurter', 590, '10'),
    ],
  };
}

/** Serverstamm → Schnellwahl der Kasse (unbekannte Farben/Icons/Sätze werden verworfen bzw. ersetzt). */
export function konfigAusStamm(
  stamm: Artikelstamm,
  profil: KassenProfil | null = null,
): SchnellwahlKonfig {
  // P10: Profil der Kasse – Reihenfolge, Farbe, Icon, sichtbar. Preis, Steuer und Sperre
  // bleiben aus dem Stamm.
  const vonProfil = (art: 'gruppe' | 'artikel') =>
    new Map(
      (profil?.kacheln ?? []).filter((k) => k.art === art).map((k) => [k.objektId, k] as const),
    );
  const pGruppe = vonProfil('gruppe');
  const pArtikel = vonProfil('artikel');
  const rang = (p: { reihenfolge: number } | undefined, i: number): [number, number] => [
    p?.reihenfolge ?? Number.MAX_SAFE_INTEGER,
    i,
  ];
  const nachRang = <T>(
    liste: readonly T[],
    eintrag: (x: T) => { reihenfolge: number } | undefined,
  ) =>
    liste
      .map((x, i) => ({ x, r: rang(eintrag(x), i) }))
      .sort((a, b) => a.r[0] - b.r[0] || a.r[1] - b.r[1])
      .map((y) => y.x);
  // Deaktivierte Gruppen und Artikel erscheinen nie an der Kasse (Betreiber-Antwort enthält sie).
  const aktiveGruppen = nachRang(
    stamm.gruppen.filter((g) => g.aktiv !== false),
    (g) => pGruppe.get(g.id),
  );
  const gruppen: Warengruppe[] = aktiveGruppen
    // Im Profil ausgeblendete Gruppe: kein Reiter, ihre Artikel bleiben per Suche/Scanner
    .filter((g) => pGruppe.get(g.id)?.sichtbar !== false)
    .map((g) => {
      const p = pGruppe.get(g.id);
      const farbe = p?.farbe ?? g.farbe;
      const icon = p?.icon ?? g.icon;
      return {
        name: g.name,
        farbe: istFarbe(farbe) ? farbe : 'blau',
        ...(istIcon(icon) ? { icon } : {}),
        ...(istSteuersatz(g.steuersatz) ? { steuersatz: g.steuersatz } : {}),
      };
    });
  const nameVonId = new Map(aktiveGruppen.map((g) => [g.id, g.name]));
  const kacheln: Schnellwahl[] = [];
  const gesperrt: GesperrteKachel[] = [];
  for (const a of nachRang(stamm.artikel, (x) => pArtikel.get(x.id))) {
    const gruppe = nameVonId.get(a.gruppeId);
    if (a.aktiv === false || gruppe === undefined || !istSteuersatz(a.steuersatz)) {
      continue;
    }
    const kachel: Schnellwahl = {
      bezeichnung: a.bezeichnung,
      bruttoCent: a.bruttoCent,
      steuersatz: a.steuersatz,
      gruppe,
      artikelId: a.id,
      ...(typeof a.ean === 'string' && EAN_FORMAT.test(a.ean) ? { ean: a.ean } : {}),
      ...(istFarbe(pArtikel.get(a.id)?.farbe ?? a.farbe)
        ? { farbe: (pArtikel.get(a.id)?.farbe ?? a.farbe) as Farbe }
        : {}),
      ...(istIcon(pArtikel.get(a.id)?.icon ?? a.icon)
        ? { icon: (pArtikel.get(a.id)?.icon ?? a.icon) as IconName }
        : {}),
      ...(pArtikel.get(a.id)?.sichtbar === false || pGruppe.get(a.gruppeId)?.sichtbar === false
        ? { ausgeblendet: true }
        : {}),
      ...(istSteuersatz(a.steuersatzAusserHaus)
        ? { steuersatzAusserHaus: a.steuersatzAusserHaus }
        : {}),
      ...(typeof a.barcode === 'string' && a.barcode !== '' ? { barcode: a.barcode } : {}),
      ...(typeof a.artikelnummer === 'string' && a.artikelnummer !== ''
        ? { artikelnummer: a.artikelnummer }
        : {}),
      ...(typeof a.syncStand === 'number' ? { artikelStand: a.syncStand } : {}),
    };
    if (a.verkaufssperre === true) {
      gesperrt.push({ ...kachel, grund: a.verkaufssperreGrund ?? 'Verkaufssperre' });
    } else {
      kacheln.push(kachel);
    }
  }
  return vervollstaendige({
    gruppen,
    kacheln,
    ...(gesperrt.length > 0 ? { gesperrt } : {}),
    basisVersion: stamm.version,
  });
}

/** Schnellwahl des Geräts → Vollabgleich für den Server (mit bekanntem Datenstand). */
export function eingabeAusKonfig(konfig: SchnellwahlKonfig): ArtikelstammEingabe {
  const voll = vervollstaendige(konfig);
  return {
    ...(voll.basisVersion === undefined ? {} : { version: voll.basisVersion }),
    gruppen: voll.gruppen.map((g) => ({ name: g.name, farbe: g.farbe, icon: g.icon ?? null })),
    // Gesperrte Artikel mitschicken, sonst würde der Vollabgleich sie deaktivieren; ihre
    // Sperre bleibt am Server unverändert (der Vollabgleich kennt die Sperrfelder nicht).
    artikel: [...voll.kacheln, ...(voll.gesperrt ?? [])].map((k) => ({
      // Server-ID mitschicken, damit der Abgleich den Artikel (und sein Lager) stabil hält.
      ...(k.artikelId === undefined ? {} : { id: k.artikelId }),
      gruppe: k.gruppe,
      bezeichnung: k.bezeichnung,
      bruttoCent: k.bruttoCent,
      steuersatz: k.steuersatz,
      steuersatzAusserHaus: k.steuersatzAusserHaus ?? null,
      farbe: k.farbe ?? null,
      icon: k.icon ?? null,
      // Weggelassen = am Server unverändert (EAN, Lager pflegt das Backoffice).
      ...(k.ean === undefined ? {} : { ean: k.ean }),
    })),
  };
}

/**
 * PB-6: Grenzen des Vollabgleichs (PUT /unternehmer/:id/artikelstamm ersetzt den ganzen Stamm).
 * Der Server nimmt höchstens 5.000 Artikel und 1 MiB Anfrage an; 900 KiB lassen Reserve.
 */
export const VOLLABGLEICH_MAX_ARTIKEL = 5000;
export const VOLLABGLEICH_MAX_BYTES = 900 * 1024;

/**
 * Darf dieser Stamm per Vollabgleich übertragen werden? Liefert den Sperrhinweis oder `null`.
 * Gezählt werden alle mitgesendeten Artikel (auch gesperrte), gemessen die UTF-8-Bytes der
 * Anfrage.
 */
export function vollabgleichSperre(eingabe: ArtikelstammEingabe): string | null {
  const anzahl = eingabe.artikel.length;
  const bytes = new TextEncoder().encode(JSON.stringify(eingabe)).length;
  if (anzahl <= VOLLABGLEICH_MAX_ARTIKEL && bytes <= VOLLABGLEICH_MAX_BYTES) {
    return null;
  }
  return `Großer Artikelstamm (${anzahl.toLocaleString('de-AT')} Artikel, ${Math.ceil(bytes / 1024).toLocaleString('de-AT')} KiB): „Auf den Server übertragen“ ist gesperrt, weil es den ganzen Stamm auf einmal ersetzt (höchstens ${VOLLABGLEICH_MAX_ARTIKEL.toLocaleString('de-AT')} Artikel und ${String(VOLLABGLEICH_MAX_BYTES / 1024)} KiB). Artikel im Backoffice über „Artikelverwaltung“ bzw. „Artikelimport“ pflegen, die Kacheln je Kasse über Schnellwahl-Profile.`;
}

export interface StammCache {
  version: number;
  konfig: SchnellwahlKonfig;
}

function cacheSchluessel(kassenId: string): string {
  return `bilanx.artikelstamm.${kassenId}`;
}

/** Zuletzt vom Server geladener Stamm (für Offline-Betrieb), sonst null. */
export function ladeStammCache(speicher: Speicher, kassenId: string): StammCache | null {
  const roh = speicher.getItem(cacheSchluessel(kassenId));
  if (roh === null) {
    return null;
  }
  try {
    const d: unknown = JSON.parse(roh);
    if (typeof d !== 'object' || d === null) {
      return null;
    }
    const c = d as Record<string, unknown>;
    if (typeof c.version !== 'number' || typeof c.konfig !== 'object' || c.konfig === null) {
      return null;
    }
    const k = c.konfig as Record<string, unknown>;
    return {
      version: c.version,
      konfig: vervollstaendige({
        gruppen: Array.isArray(k.gruppen)
          ? k.gruppen.map(zuGruppe).filter((g): g is Warengruppe => g !== null)
          : [],
        kacheln: Array.isArray(k.kacheln)
          ? k.kacheln.map(zuKachel).filter((x): x is Schnellwahl => x !== null)
          : [],
        ...(Array.isArray(k.gesperrt)
          ? {
              gesperrt: k.gesperrt.map(zuGesperrt).filter((x): x is GesperrteKachel => x !== null),
            }
          : {}),
        basisVersion: c.version,
      }),
    };
  } catch {
    return null;
  }
}

/**
 * Größter Stamm (Zeichen JSON), der zusätzlich im localStorage liegt. Größere Stämme
 * (z. B. 20.000 Artikel) liegen nur in IndexedDB – der localStorage fasst meist nur 5 MB
 * je Herkunft und wird auch für Bon-Sicherung, Belegnummern usw. gebraucht.
 */
export const MAX_STAMM_LOKAL = 1_000_000;

/**
 * Zuletzt geladener Stamm je Kasse im Arbeitsspeicher (Verkauf, Terminbuch, Scanner) – je
 * Ablageobjekt (im Browser: localStorage), damit Speicher und Arbeitsspeicher zusammengehören.
 */
let stammImSpeicher = new WeakMap<Speicher, Map<string, StammCache>>();

function imSpeicher(speicher: Speicher, kassenId: string): StammCache | undefined {
  return stammImSpeicher.get(speicher)?.get(kassenId);
}

function merkeImSpeicher(speicher: Speicher, kassenId: string, cache: StammCache): void {
  let m = stammImSpeicher.get(speicher);
  if (m === undefined) {
    m = new Map();
    stammImSpeicher.set(speicher, m);
  }
  m.set(kassenId, cache);
}

/**
 * Stamm sichern: Arbeitsspeicher sofort, localStorage nur bis MAX_STAMM_LOKAL (sonst ersetzt
 * ein kleiner Hinweis den alten Eintrag, damit kein veralteter Stamm bleibt), IndexedDB über
 * `ablage` (asynchron; Fehler werden geschluckt – der Server bleibt die Quelle).
 */
export function speichereStammCache(
  speicher: Speicher,
  kassenId: string,
  cache: StammCache,
  ablage: StammAblage | null = null,
): void {
  // Nur vorwärts: eine verspätete ältere Antwort (Einrichtung, Verkauf, zweite Quelle)
  // überschreibt nie einen neueren Stand – sonst zeigte die Kasse alte Preise (Codex P8).
  const bekannt = stammCacheVersion(speicher, kassenId);
  if (bekannt !== null && cache.version < bekannt) {
    return;
  }
  merkeImSpeicher(speicher, kassenId, cache);
  const json = JSON.stringify(cache);
  const hinweis = JSON.stringify({ version: cache.version, inIndexedDb: true });
  try {
    speicher.setItem(cacheSchluessel(kassenId), json.length <= MAX_STAMM_LOKAL ? json : hinweis);
  } catch {
    // Speicher voll: nur den Hinweis ablegen – der Stamm liegt im Arbeitsspeicher und in
    // IndexedDB, ein älterer Stamm im localStorage darf nicht stehen bleiben.
    try {
      speicher.setItem(cacheSchluessel(kassenId), hinweis);
    } catch {
      // auch dafür kein Platz: der Arbeitsspeicher bleibt maßgeblich (aktuelleSchnellwahl)
    }
  }
  if (ablage !== null) {
    void ablage.schreibe(kassenId, cache).catch(() => undefined);
  }
}

/**
 * Stamm aus IndexedDB (Offline-Start bei großen Stämmen). Liefert den jüngsten Stand aus
 * IndexedDB und Arbeitsspeicher; null, wenn IndexedDB leer ist oder älter als der Stand im
 * localStorage bzw. Arbeitsspeicher (dann bleibt der Aufrufer beim aktuellen Stand).
 */
export async function ladeStammCacheAsync(
  ablage: StammAblage | null,
  kassenId: string,
  speicher: Speicher,
): Promise<StammCache | null> {
  if (ablage === null) {
    return null;
  }
  try {
    const roh = await ablage.lies(kassenId);
    if (roh === undefined || roh === null) {
      return null;
    }
    const c = ladeStammCache(
      {
        getItem: () => JSON.stringify(roh),
        setItem: () => undefined,
      },
      kassenId,
    );
    if (c === null) {
      return null;
    }
    // Älter als ein bekannter Stand (z. B. IndexedDB-Schreiben gescheitert)? Dann nicht
    // verwenden – sonst würde ein inzwischen gesperrter Artikel wieder verkaufbar.
    const lokal = lokaleVersion(speicher, kassenId);
    const merk = imSpeicher(speicher, kassenId);
    if ((lokal !== null && c.version < lokal) || (merk !== undefined && c.version < merk.version)) {
      return null;
    }
    merkeImSpeicher(speicher, kassenId, c);
    return c;
  } catch {
    return null;
  }
}

/**
 * Schnellwahl, mit der die Kasse verkauft: zuletzt geladener Serverstamm, sonst die
 * Konfiguration dieses Geräts. Verkauf und Terminbuch nutzen dieselbe Quelle, damit
 * eine Leistung aus dem Termin dieselbe Kachel (Preis, Steuersatz, Artikel) findet.
 */
export function aktuelleSchnellwahl(speicher: Speicher, kassenId: string): SchnellwahlKonfig {
  const lokal = ladeStammCache(speicher, kassenId);
  const version = lokaleVersion(speicher, kassenId);
  const merk = imSpeicher(speicher, kassenId);
  // Der jüngste Serverstand gewinnt; der Arbeitsspeicher ist neuer, wenn das Schreiben in den
  // localStorage scheiterte (auch ganz ohne lokalen Eintrag).
  if (merk !== undefined && (version === null || merk.version >= version)) {
    return merk.konfig;
  }
  if (lokal !== null) {
    return lokal.konfig;
  }
  if (version !== null) {
    // Großer Serverstamm nur in IndexedDB, noch nicht geladen: lieber leer als die
    // (veraltete) Gerätekonfiguration – nie an einem neueren Stamm vorbei verkaufen.
    return { gruppen: [], kacheln: [] };
  }
  return ladeSchnellwahl(speicher, kassenId);
}

/** Jüngste bekannte Version des Schnellwahl-Caches (Arbeitsspeicher oder localStorage). */
export function stammCacheVersion(speicher: Speicher, kassenId: string): number | null {
  const merk = imSpeicher(speicher, kassenId)?.version ?? null;
  const lokal = lokaleVersion(speicher, kassenId);
  if (merk === null) {
    return lokal;
  }
  return lokal === null ? merk : Math.max(merk, lokal);
}

/** Version des Serverstamms laut localStorage (voller Eintrag oder IndexedDB-Hinweis). */
function lokaleVersion(speicher: Speicher, kassenId: string): number | null {
  try {
    const d: unknown = JSON.parse(speicher.getItem(cacheSchluessel(kassenId)) ?? 'null');
    if (typeof d !== 'object' || d === null) {
      return null;
    }
    const v = (d as Record<string, unknown>).version;
    return typeof v === 'number' ? v : null;
  } catch {
    return null;
  }
}

/** Nur für Tests: Arbeitsspeicher-Zwischenspeicher leeren. */
export function vergissStammImSpeicher(): void {
  stammImSpeicher = new WeakMap();
}

export type CodeTreffer =
  | { art: 'artikel'; kachel: Schnellwahl; ueber: 'barcode' | 'ean' | 'artikelnummer' }
  | {
      art: 'gesperrt';
      kachel: GesperrteKachel;
      grund: string;
      ueber: 'barcode' | 'ean' | 'artikelnummer';
    };

interface CodeIndex {
  barcode: Map<string, CodeTreffer>;
  ean: Map<string, CodeTreffer>;
  artikelnummer: Map<string, CodeTreffer>;
}

const codeIndizes = new WeakMap<SchnellwahlKonfig, CodeIndex>();

function codeIndex(konfig: SchnellwahlKonfig): CodeIndex {
  const vorhanden = codeIndizes.get(konfig);
  if (vorhanden !== undefined) {
    return vorhanden;
  }
  const index: CodeIndex = { barcode: new Map(), ean: new Map(), artikelnummer: new Map() };
  const eintragen = (k: Schnellwahl, gesperrt: GesperrteKachel | null): void => {
    for (const ueber of ['barcode', 'ean', 'artikelnummer'] as const) {
      const code = k[ueber];
      if (code === undefined || index[ueber].has(code)) {
        continue; // erster Treffer gewinnt (verkaufbare vor gesperrten)
      }
      index[ueber].set(
        code,
        gesperrt === null
          ? { art: 'artikel', kachel: k, ueber }
          : { art: 'gesperrt', kachel: gesperrt, grund: gesperrt.grund, ueber },
      );
    }
  };
  for (const k of konfig.kacheln) {
    eintragen(k, null);
  }
  for (const g of konfig.gesperrt ?? []) {
    eintragen(g, g);
  }
  codeIndizes.set(konfig, index);
  return index;
}

/**
 * Erster Artikel zu einem Code: exakt über Barcode, dann EAN, dann Artikelnummer – führende
 * Nullen zählen. Gesperrte Artikel kommen als `art: 'gesperrt'` mit Grund zurück; null =
 * unbekannt.
 *
 * **Nicht für den Verkauf:** Liefert bei mehrdeutigen Codes still den ersten Treffer. Verkauf,
 * Suchfeld und Handsuche verwenden {@link codeTrefferliste} (alle Treffer, PB-1).
 */
export function findeArtikelZuCode(konfig: SchnellwahlKonfig, code: string): CodeTreffer | null {
  const c = code.trim();
  if (c === '') {
    return null;
  }
  const index = codeIndex(konfig);
  return index.barcode.get(c) ?? index.ean.get(c) ?? index.artikelnummer.get(c) ?? null;
}

/** Code-Feld eines Artikels, über das ein Treffer zustande kam. */
export type CodeFeld = 'barcode' | 'ean' | 'artikelnummer';

/** Ein Artikel zu einem Code (PB-1): verkaufbar (`gesperrt: null`) oder gesperrt mit Grund. */
export interface CodeListenTreffer {
  kachel: Schnellwahl;
  gesperrt: GesperrteKachel | null;
  ueber: CodeFeld;
  /** true = erst über die GTIN-Normalisierung (UPC-A/EAN-13/GTIN-14) gefunden. */
  normalisiert: boolean;
}

interface CodeListenIndex {
  /** exakter Code (Barcode, EAN, Artikelnummer) → alle Artikel */
  exakt: Map<string, CodeListenTreffer[]>;
  /** EAN-13-Form (aus Barcode und EAN, nie Artikelnummer) → alle Artikel */
  gtin: Map<string, CodeListenTreffer[]>;
}

/**
 * Zweistufiger Cache: Kachelliste → (gesperrte Liste bzw. „ohne“) → Index. So bauen Aufrufer
 * mit unterschiedlicher Sperrliste für dieselben Kacheln nicht abwechselnd neu (R1, H2).
 */
const codeListenIndizes = new WeakMap<
  readonly Schnellwahl[],
  { ohne?: CodeListenIndex; mit: WeakMap<readonly GesperrteKachel[], CodeListenIndex> }
>();
const CODE_FELDER: readonly CodeFeld[] = ['barcode', 'ean', 'artikelnummer'];

function anhaengen(
  map: Map<string, CodeListenTreffer[]>,
  schluessel: string,
  t: CodeListenTreffer,
): void {
  const liste = map.get(schluessel);
  if (liste === undefined) {
    map.set(schluessel, [t]);
  } else if (!liste.some((x) => x.kachel === t.kachel)) {
    liste.push(t);
  }
}

function codeListenIndex(
  kacheln: readonly Schnellwahl[],
  gesperrt: readonly GesperrteKachel[] | undefined,
): CodeListenIndex {
  let stufe = codeListenIndizes.get(kacheln);
  if (stufe === undefined) {
    stufe = { mit: new WeakMap() };
    codeListenIndizes.set(kacheln, stufe);
  }
  const vorhanden = gesperrt === undefined ? stufe.ohne : stufe.mit.get(gesperrt);
  if (vorhanden !== undefined) {
    return vorhanden;
  }
  const index: CodeListenIndex = { exakt: new Map(), gtin: new Map() };
  const eintragen = (k: Schnellwahl, g: GesperrteKachel | null): void => {
    for (const ueber of CODE_FELDER) {
      const code = k[ueber]?.trim();
      if (code === undefined || code === '') {
        continue;
      }
      anhaengen(index.exakt, code, { kachel: k, gesperrt: g, ueber, normalisiert: false });
      if (ueber !== 'artikelnummer') {
        const ean = normalisiereEan(code);
        if (ean !== null) {
          anhaengen(index.gtin, ean, { kachel: k, gesperrt: g, ueber, normalisiert: true });
        }
      }
    }
  };
  for (const k of kacheln) {
    eintragen(k, null);
  }
  for (const g of gesperrt ?? []) {
    eintragen(g, g);
  }
  if (gesperrt === undefined) {
    stufe.ohne = index;
  } else {
    stufe.mit.set(gesperrt, index);
  }
  return index;
}

/** Index im Voraus bauen (z. B. im Leerlauf nach dem Laden des Stamms), nicht beim ersten Scan. */
export function baueCodeIndex(
  kacheln: readonly Schnellwahl[],
  gesperrt: readonly GesperrteKachel[] | undefined,
): void {
  codeListenIndex(kacheln, gesperrt);
}

/**
 * PB-1: **alle** Artikel zu einem gescannten oder getippten Code – gemeinsame Grundlage für
 * Scanner, Suchfeld-Scan und Handsuche.
 *
 * 1. exakt über Barcode, EAN und Artikelnummer (Text, führende Nullen zählen, auch nicht
 *    numerische Codes und Barcodes mit „falscher“ Prüfziffer);
 * 2. danach die GTIN-Normalisierung (`normalisiereEan`: UPC-A, EAN-13 und GTIN-14 mit führender
 *    0 sind derselbe Code) – nur gegen Barcode und EAN, nie gegen die Artikelnummer.
 *
 * Ein exakter Treffer beendet die Suche nicht: Steht dieselbe GTIN in anderer Schreibweise bei
 * einem anderen Artikel, gehört der gescannte Code zu beiden (mehrdeutig). Jeder Artikel kommt
 * höchstens einmal vor, exakte Treffer zuerst. Verkaufbare und gesperrte Artikel (mit Grund)
 * stehen in derselben Liste – der Aufrufer entscheidet (nie still der erste).
 * Der Index wird je Kachelliste einmal gebaut (danach O(1) je Code, auch bei 20.000 Artikeln).
 */
export function codeTrefferliste(
  kacheln: readonly Schnellwahl[],
  gesperrt: readonly GesperrteKachel[] | undefined,
  code: string,
): CodeListenTreffer[] {
  const c = code.trim();
  if (c === '') {
    return [];
  }
  const index = codeListenIndex(kacheln, gesperrt);
  const ergebnis = [...(index.exakt.get(c) ?? [])];
  const ean = normalisiereEan(c);
  if (ean !== null) {
    for (const t of index.gtin.get(ean) ?? []) {
      if (!ergebnis.some((x) => x.kachel === t.kachel)) {
        ergebnis.push(t);
      }
    }
  }
  return ergebnis;
}

/** Höchstzahl gezeichneter Kacheln je Ansicht (große Stämme: Rest über Suche/Scanner). */
export const MAX_KACHELN_ANZEIGE = 300;

/** Gesperrte Artikel, die zur Suche passen (Anzeige ohne Verkaufsmöglichkeit). */
export function sucheGesperrte(konfig: SchnellwahlKonfig, text: string): GesperrteKachel[] {
  const g = konfig.gesperrt ?? [];
  return text.trim() === '' ? [] : (sucheKacheln(g, text) as GesperrteKachel[]);
}

/**
 * Handsuche über alle Gruppen (PB-1): Ist der Text ein Artikelcode (exakt Barcode, EAN oder
 * Artikelnummer, auch nicht numerisch, bzw. dieselbe GTIN als UPC-A/EAN-13/GTIN-14), zählen nur
 * diese Treffer – gehört er (auch) einem gesperrten Artikel, wird kein anderer Artikel per
 * Präfix angeboten. Sonst treffen Ziffern den Anfang von EAN, Barcode bzw. Artikelnummer (unter
 * 8 Stellen), anderer Text die Bezeichnung (Teiltext, ohne Groß/Klein). Leerer Text = kein
 * Filter.
 */
export function sucheKacheln(kacheln: readonly Schnellwahl[], text: string): Schnellwahl[] {
  const roh = text.trim();
  if (roh === '') {
    return [...kacheln];
  }
  const code = codeTrefferliste(kacheln, gesperrteZu.get(kacheln), roh);
  if (code.length > 0) {
    // gesperrte Treffer zeigt `sucheGesperrte` (mit Grund, nicht wählbar)
    return code.filter((t) => t.gesperrt === null).map((t) => t.kachel);
  }
  const t = roh.toLowerCase();
  if (/^\d+$/.test(t)) {
    // Ab 8 Ziffern (gescannter Barcode) gilt nur der genaue Treffer.
    if (t.length >= 8) {
      return [];
    }
    return kacheln.filter(
      (k) =>
        k.ean?.startsWith(t) === true ||
        k.barcode?.startsWith(t) === true ||
        k.artikelnummer?.startsWith(t) === true,
    );
  }
  return kacheln.filter((k) => k.bezeichnung.toLowerCase().includes(t));
}

/** Kachel → Bonposition (Menge 1); „außer Haus" nutzt den abweichenden Steuersatz, wenn gepflegt. */
export function positionAus(k: Schnellwahl, ausserHaus = false): Position {
  const abweichend =
    ausserHaus && k.steuersatzAusserHaus !== undefined && k.steuersatzAusserHaus !== k.steuersatz;
  return {
    bezeichnung: abweichend ? `${k.bezeichnung} (außer Haus)` : k.bezeichnung,
    menge: 1,
    bruttoCent: k.bruttoCent,
    steuersatz: abweichend ? (k.steuersatzAusserHaus ?? k.steuersatz) : k.steuersatz,
    ...(k.artikelId === undefined ? {} : { artikelId: k.artikelId }),
    ...(k.artikelId === undefined || k.artikelStand === undefined
      ? {}
      : { artikelStand: k.artikelStand }),
    ...(k.artikelId === undefined ? {} : { einzelCent: k.bruttoCent }),
  };
}

/**
 * Position in den Bon legen: gleiche Bezeichnung, gleicher Einzelpreis, gleicher
 * Steuersatz und gleicher Artikel werden zu einer Zeile mit höherer Menge
 * zusammengefasst – verschiedene Artikel gleichen Namens bleiben getrennt
 * (sonst fehlte der Lagerabgang des zweiten).
 */
export function inBon(positionen: readonly Position[], neu: Position): Position[] {
  const einzelNeu = einzelpreis(neu);
  const i = positionen.findIndex(
    (p) =>
      p.bezeichnung === neu.bezeichnung &&
      p.steuersatz === neu.steuersatz &&
      p.artikelId === neu.artikelId &&
      einzelpreis(p) === einzelNeu,
  );
  if (i < 0) {
    return [...positionen, neu];
  }
  const alt = positionen[i];
  if (alt === undefined) {
    return [...positionen, neu];
  }
  const menge = alt.menge + neu.menge;
  return positionen.map((p, j) =>
    j === i ? { ...p, menge, bruttoCent: Math.round(einzelNeu * menge) } : p,
  );
}

/** Einzelpreis einer Bonzeile (Cent, ggf. gebrochen bei krummen Mengen). */
export function einzelpreis(p: Position): number {
  if (p.einzelCent !== undefined) {
    return p.einzelCent;
  }
  return p.menge === 0 ? 0 : p.bruttoCent / p.menge;
}

/** Zeilenbetrag für eine Menge – mit Zeilenrabatt nach derselben Formel wie beim Setzen. */
export function zeilenbetrag(p: Position, menge: number): number {
  const n = p.zeilenNachlass;
  if (n === undefined) {
    return Math.round(einzelpreis(p) * menge);
  }
  const voll = n.basisCent * menge;
  return Math.round(voll) - Math.round((voll * n.prozent) / 100);
}

/** Menge einer Bonzeile ändern (0 entfernt die Zeile); Betrag folgt dem Einzelpreis. */
export function mengeSetzen(
  positionen: readonly Position[],
  index: number,
  menge: number,
): Position[] {
  if (menge <= 0) {
    return positionen.filter((_, j) => j !== index);
  }
  // Mit Zeilenrabatt vom Preis vor dem Nachlass aus rechnen – sonst wandert der
  // Rundungsrest bei jeder Mengenänderung weiter (3 → 2 → 3 Stück ergäbe +1 Cent).
  return positionen.map((p, j) =>
    j === index ? { ...p, menge, bruttoCent: zeilenbetrag(p, menge) } : p,
  );
}

/*
 * Änderungen im Schnellwahl-Editor, die noch nicht am Server sind. Liegt am
 * Server ein Artikelstamm, zeigt der Verkauf diesen; lokale Änderungen wirken
 * erst nach dem Übertragen. Die Markierung sorgt dafür, dass die Einrichtung
 * darauf hinweist, statt den Serverstand stillschweigend zu übernehmen.
 */
function ungespeichertSchluessel(kassenId: string): string {
  return `bilanx.schnellwahl.ungespeichert.${kassenId}`;
}

export function istUngespeichert(speicher: Speicher, kassenId: string): boolean {
  return speicher.getItem(ungespeichertSchluessel(kassenId)) === '1';
}

export function markiereUngespeichert(speicher: Speicher, kassenId: string, wert: boolean): void {
  speicher.setItem(ungespeichertSchluessel(kassenId), wert ? '1' : '');
}

/** Markierung noch nie gesetzt (Gerät aus der Zeit vor der Markierung)? */
export function ungespeichertUnbekannt(speicher: Speicher, kassenId: string): boolean {
  return speicher.getItem(ungespeichertSchluessel(kassenId)) === null;
}

/** Inhaltlicher Vergleich zweier Konfigurationen (ohne Server-IDs und Reihenfolge-Details). */
export function gleicheKonfig(a: SchnellwahlKonfig, b: SchnellwahlKonfig): boolean {
  const kern = (k: SchnellwahlKonfig): string =>
    JSON.stringify({
      g: k.gruppen.map((x) => [x.name, x.farbe, x.icon ?? '']),
      k: k.kacheln.map((x) => [
        x.gruppe,
        x.bezeichnung,
        x.bruttoCent,
        x.steuersatz,
        x.steuersatzAusserHaus ?? '',
        x.ean ?? '',
      ]),
    });
  return kern(a) === kern(b);
}

/** Höchste Stückzahl, die als Vorgabe vor einer Kachel gilt (größere Eingaben sind eher ein Betrag). */
export const MAX_VORGABE_MENGE = 999;

/**
 * Menge für die nächste Kachel: getippte Ziffern im Ziffernblock (5 → 5 Stück),
 * sonst der Mengenzähler; ungültig/zu groß → 1. Die Ziffern liegen als Cent vor,
 * weil der Ziffernblock von rechts einschiebt – die Zahl ist dieselbe.
 */
export function vorgabeMenge(zifferCent: number, zaehler: number): number {
  if (Number.isInteger(zifferCent) && zifferCent >= 1 && zifferCent <= MAX_VORGABE_MENGE) {
    return zifferCent;
  }
  return Number.isInteger(zaehler) && zaehler >= 1 ? zaehler : 1;
}

/** Kachel mit Menge als Bonposition (Betrag = Einzelpreis × Menge). */
export function positionMitMenge(k: Schnellwahl, menge: number, ausserHaus = false): Position {
  const p = positionAus(k, ausserHaus);
  return { ...p, menge, bruttoCent: p.bruttoCent * menge };
}

/**
 * Stückzahl einer Kachel im aktuellen Bon (Anzeige „Im Bon“ auf der Kachel). Artikel aus dem
 * Artikelstamm werden über die Artikel-ID gezählt – auch nach einer Preisänderung je Zeile;
 * Kacheln ohne Artikel-ID über die Bezeichnung, auch in der Variante „(außer Haus)“.
 * Rabattzeilen und frei erfasste Beträge zählen nie mit.
 */
export function mengeImBon(positionen: readonly Position[], k: Schnellwahl): number {
  const namen = [k.bezeichnung, `${k.bezeichnung} (außer Haus)`];
  return positionen.reduce((n, p) => {
    const passt =
      k.artikelId !== undefined
        ? p.artikelId === k.artikelId
        : p.artikelId === undefined && namen.includes(p.bezeichnung);
    return passt && p.bruttoCent >= 0 ? n + p.menge : n;
  }, 0);
}
